import { TextField, type TextFieldProps } from '@mui/material';
import { _ } from '/@/locale';
import { validateEmailV2 } from '/@/utils';
import { getGrowerIdentifierCopy } from './grower-identifier';

export type EmailIdentifierFormatError = 'empty' | 'invalid' | 'own_email';

/** Why a grower email identifier is rejected before asking the backend, or null if it is fine. */
export function emailIdentifierFormatError(email: string, ownEmail?: string): EmailIdentifierFormatError | null {
    const trimmed = email.trim();
    if (!trimmed) return 'empty';
    if (!validateEmailV2(trimmed)) return 'invalid';
    if (ownEmail && trimmed.toLowerCase() === ownEmail.trim().toLowerCase()) return 'own_email';
    return null;
}

/**
 * Validates grower email identifier format and, when provided, that it doesn't match
 * the current user's own node email.
 * Returns an error message or null if valid.
 */
export function validateGrowerIdentifierEmail(email: string, ownEmail?: string): string | null {
    switch (emailIdentifierFormatError(email, ownEmail)) {
        case 'empty':
            return _('Ingresá el correo del productor');
        case 'invalid':
            return getGrowerIdentifierCopy('email').invalidFormat;
        case 'own_email':
            return _('El email del productor no puede ser el mismo que el de tu usuario');
        default:
            return null;
    }
}

export type EmailIdentifierTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
    value: string;
    onChange: (email: string) => void;
};

const EmailIdentifierTextField = ({
    value,
    onChange,
    error,
    helperText,
    InputProps,
    ...textFieldProps
}: EmailIdentifierTextFieldProps) => {
    const copy = getGrowerIdentifierCopy('email');

    return (
        <TextField
            label={copy.fieldLabel}
            fullWidth
            size='small'
            type='email'
            placeholder='productor@mail.com'
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={error}
            helperText={helperText != null ? helperText : copy.fieldHelp}
            InputProps={InputProps}
            {...textFieldProps}
        />
    );
};

export default EmailIdentifierTextField;
